
import { useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { CourseDetails } from "./CourseDetails";
import { CourseModules } from "./CourseModules";
import { CourseLoading } from "./courses/CourseLoading";
import { CourseNotFound } from "./courses/CourseNotFound";
import { CourseError } from "./courses/CourseError";
import { ExamQuestionsContent } from "./exam-questions";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "@/components/ui/use-toast";
import { ErrorBoundary } from "@/components/ErrorBoundary";

// Define the course type to help TypeScript understand the expected structure
type CourseType = {
  id: string;
  course_name: string;
  course_type: string;
  description: string;
  duration_hours: number;
  price: number;
  original_price: number | null;
  state_fee: number | null;
  language: string;
  benefits?: any;
  footer_text?: any;
  state?: string;
  created_at?: string;
  updated_at?: string;
};

export function CourseProfile() {
  const { id } = useParams();
  const queryClient = useQueryClient();

  console.log('CourseProfile loaded with ID:', id);

  const { 
    data: course, 
    isLoading, 
    error, 
    refetch 
  } = useQuery({
    queryKey: ['course', id],
    queryFn: async () => {
      console.log('Fetching course details for ID:', id);
      
      if (!id) {
        console.error('No course ID provided');
        toast.error("Error", "Course ID is missing");
        throw new Error('No course ID provided');
      }
      
      // Using the custom RPC function to bypass RLS
      const { data, error } = await supabase
        .rpc('get_course_by_id', { course_id: id })
        .single();
      
      if (error) {
        console.error('Error fetching course:', error);
        toast.error("Failed to load course details", error.message);
        throw error;
      }
      
      console.log('Course data fetched successfully:', data);
      return data as CourseType;
    },
    retry: 2,
    retryDelay: 1000,
    enabled: !!id
  });

  // Handle different states
  if (isLoading) {
    return <CourseLoading />;
  }

  if (error) {
    return <CourseError error={error} onRetry={refetch} />;
  }

  if (!course) {
    return <CourseNotFound />;
  }

  return (
    <ErrorBoundary 
      fallback={
        <div className="p-8 text-center">
          <p className="text-lg text-destructive mb-4">Something went wrong</p>
          <button 
            className="underline text-primary" 
            onClick={() => window.location.reload()}
          >
            Reload page
          </button>
        </div>
      }
      onError={(error) => {
        console.error("Error in CourseProfile:", error);
        toast.error("Error", "Something went wrong while displaying the course");
      }}
    >
      <div className="p-8 space-y-8">
        <CourseDetails course={course} />
        
        <Tabs defaultValue="modules" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="modules">Course Modules</TabsTrigger>
            <TabsTrigger value="questions">Exam Questions</TabsTrigger>
          </TabsList>
          
          <TabsContent value="modules" className="mt-6">
            <CourseModules courseId={id!} />
          </TabsContent>
          
          <TabsContent value="questions" className="mt-6">
            <ExamQuestionsContent courseId={id!} />
          </TabsContent>
        </Tabs>
      </div>
    </ErrorBoundary>
  );
}
